import React from 'react';
import CustomModal from '../reusable/CustomModal';
import SchoolReportStatus from './SchoolReportStatus';
import './SchoolReportWorkflowModal.css';

interface SchoolReportWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  userRole?: string;
}

const SchoolReportWorkflowModal = ({ isOpen, onClose, userRole }: SchoolReportWorkflowModalProps) => {
  return (
    <CustomModal
      isOpen={isOpen}
      handleClose={onClose}
      title="School report approval workflow"
      icon={false}
      maxWidth="md"
    >
      <p className="school-report-modal-description">
        The status column shows the current stage of each school report, from creation to final approval.
      </p>
      <div className="school-report-workflow-steps">
        <div className="workflow-step">
          <div className="step-indicator">
            <span className="step-number">1</span>
          </div>
          <div className="step-content">
            <h3 className="step-title">Report not generated</h3>
            <div className="step-status">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_NOT_GENERATED" userRole={userRole} />
            </div>
            <p className="step-description">The school report has not been created yet.</p>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-indicator">
            <span className="step-number">2</span>
          </div>
          <div className="step-content">
            <h3 className="step-title">Report generated</h3>
            <div className="step-status">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_GENERATED" userRole={userRole} />
            </div>
            <p className="step-description">
              The school report exists, but the internal review process has not started.
            </p>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-indicator">
            <span className="step-number">3</span>
          </div>
          <div className="step-content">
            <h3 className="step-title">Internal review</h3>
            <div className="step-status">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_READY_FOR_INTERNAL_REVIEW" userRole={userRole} />
            </div>
            <p className="step-description">
              The report is ready for review by the Lead Facilitator. (An XQ Admin can approve on the Lead Facilitator's
              behalf if needed.)
            </p>
            <div className="step-status step-status-secondary">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_APPROVED_BY_LEAD_FACILITATOR" userRole={userRole} />
            </div>
            <p className="step-description step-description-secondary">
              Once the Lead Facilitator approves the report, the status changes to Approved by Lead Facilitator.
            </p>
            <div className="step-status step-status-secondary">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_REJECTED" userRole={userRole} />
            </div>
            <p className="step-description step-description-secondary">
              An issue has been reported by a Lead Facilitator, XQ Admin, or another internal XQ stakeholder during the
              review process.
            </p>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-indicator">
            <span className="step-number">4</span>
          </div>
          <div className="step-content">
            <h3 className="step-title">District review</h3>
            <div className="step-status">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_READY_FOR_DISTRICT_REVIEW" userRole={userRole} />
            </div>
            <p className="step-description">
              Once the Lead Facilitator approves the report, the XQ admin and any internal XQ stakeholders need to
              approve the report as well before moving on to reviewing with the District.
            </p>
            <div className="step-status step-status-secondary">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_REJECTED" userRole={userRole} />
            </div>
            <p className="step-description step-description-secondary">
              An issue has been reported by a Lead Facilitator, XQ Admin, or another internal XQ stakeholder during the
              review process.
            </p>
          </div>
        </div>

        <div className="workflow-step">
          <div className="step-indicator">
            <span className="step-number">5</span>
          </div>
          <div className="step-content">
            <h3 className="step-title">Finalized</h3>
            <div className="step-status">
              <span className="status-label">Status:</span>
              <SchoolReportStatus status="SCHOOL_REPORT_FINALIZED" userRole={userRole} />
            </div>
            <p className="step-description">The report has been approved both internally and by the district.</p>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default SchoolReportWorkflowModal;
